import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Typography,
  Box,
  Alert,
} from "@mui/material";
import Swal from "sweetalert2";
import api from "../../api/axios";
import LoadingPage from "../../Components/Common/Loading/LoadingPage";

const DeleteCaseTypeModal = ({
  open,
  caseTypeId,
  caseTypeName,
  companyId,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [loadingPreValidation, setLoadingPreValidation] = useState(false);
  const [preValidation, setPreValidation] = useState(null);
  const [otherCaseTypes, setOtherCaseTypes] = useState([]);
  const [selectedNewCaseType, setSelectedNewCaseType] = useState(null);

  useEffect(() => {
    if (open && caseTypeId) {
      setSelectedNewCaseType(null);
      getDeletePreValidation();
    }
  }, [open, caseTypeId]);

  const getDeletePreValidation = async () => {
    setLoadingPreValidation(true);
    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/casestypes/pre-validate-delete`,
        { id: caseTypeId },
      );

      setPreValidation(data);
      setOtherCaseTypes(data.otherCaseTypes || []);
    } catch (error) {
      console.error("Error en pre-validación de eliminación:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo validar la eliminación",
      });
      onClose();
    } finally {
      setLoadingPreValidation(false);
    }
  };

  const handleDelete = async () => {
    if (preValidation.affectedCases > 0 || preValidation.affectedTasks > 0) {
      if (!selectedNewCaseType) {
        Swal.fire({
          icon: "warning",
          title: "Reasignación requerida",
          text: "Debe seleccionar un tipo de caso para reasignar los expedientes y tareas",
        });
        return;
      }

      // Reasignar antes de eliminar
      setLoading(true);
      try {
        await api.post(
          `${process.env.REACT_APP_API_URL}/casestypes/reassign-before-delete`,
          {
            id: caseTypeId,
            newCaseTypeId: selectedNewCaseType.id,
          },
        );

        // Ahora eliminar el tipo de caso
        await api.post(`${process.env.REACT_APP_API_URL}/casestypes/destroy`, {
          id: caseTypeId,
        });

        Swal.fire({
          icon: "success",
          title: "Eliminado",
          text: `El tipo de caso se ha eliminado y los registros han sido reasignados correctamente.`,
        });

        onSuccess();
        onClose();
      } catch (error) {
        console.error("Error en eliminación y reasignación:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.response?.data?.message ||
            "Ocurrió un error durante la eliminación",
        });
      } finally {
        setLoading(false);
      }
    } else {
      // Sin registros asociados, eliminar directamente
      setLoading(true);
      try {
        await api.post(`${process.env.REACT_APP_API_URL}/casestypes/destroy`, {
          id: caseTypeId,
        });

        Swal.fire({
          icon: "success",
          title: "Eliminado",
          text: "El tipo de caso se ha eliminado correctamente.",
        });

        onSuccess();
        onClose();
      } catch (error) {
        console.error("Error en eliminación:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text:
            error.response?.data?.message ||
            "Ocurrió un error durante la eliminación",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Confirmar eliminación: <strong>{caseTypeName}</strong>
      </DialogTitle>
      <DialogContent>
        {loadingPreValidation ? (
          <Box sx={{ py: 1 }}>
            <LoadingPage isLoading={true} heightProp="160px" />
          </Box>
        ) : (
          preValidation && (
            <Box sx={{ mt: 2 }}>
              {preValidation.affectedCases > 0 ||
              preValidation.affectedTasks > 0 ? (
                <>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {/* Este tipo de caso tiene registros asociados que serán
                    reasignados: */}
                    Se han encontrado:
                    {preValidation.affectedCases > 0 && (
                      <strong style={{ marginLeft: "10px" }}>
                        {preValidation.affectedCases} expediente(s) asociado(s)
                        a este tipo de caso.
                      </strong>
                    )}
                    {/* <ul style={{ marginTop: "8px", marginBottom: 0 }}> */}
                    {/* {preValidation.affectedTasks > 0 && (
                        <li>
                          <strong>{preValidation.affectedTasks}</strong>{" "}
                          tarea(s)
                        </li>
                      )} */}
                    {/* </ul> */}
                  </Alert>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    Por favor, elimine primero todos los expedientes asociados
                    antes de eliminar este elemento.
                  </Typography>

                  {/* <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    Seleccione a cuál tipo de caso desea reasignarlos:
                  </Typography>

                  <Autocomplete
                    size="small"
                    options={otherCaseTypes}
                    getOptionLabel={(option) => option.case_type}
                    value={selectedNewCaseType}
                    onChange={(_, value) => setSelectedNewCaseType(value)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tipo de caso destino"
                        variant="outlined"
                        fullWidth
                        required
                      />
                    )}
                    sx={{ mb: 2 }}
                  /> */}
                </>
              ) : (
                <Alert severity="info">
                  Este tipo de caso no tiene registros asociados y se puede
                  eliminar sin problemas.
                </Alert>
              )}
            </Box>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={
            loading ||
            (preValidation &&
              (preValidation.affectedCases > 0 ||
                preValidation.affectedTasks > 0) &&
              !selectedNewCaseType)
          }>
          {loading ? "Procesando..." : "Eliminar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteCaseTypeModal;
